import { ReactNode, forwardRef, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { cn } from '../../utils/cn';
import { Text } from '../Text';

export interface TextareaProps {
  size?: 'sm' | 'md';
  /** Left side decorator/icon */
  leftDecorator?: ReactNode;
  /** Right side decorator/icon */
  rightDecorator?: ReactNode;
  /** Whether the textarea is disabled */
  disabled?: boolean;
  /** Whether the textarea has an error state */
  isError?: boolean;
  /** Label text displayed above the textarea */
  label?: string | ReactNode;
  /** Hint text displayed below the textarea */
  hintText?: string;
  /** Current textarea value */
  value?: string;
  /** Change handler that will be debounced */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Debounce delay in milliseconds (default: 300) */
  debounceMs?: number;
  /** Additional CSS classes */
  className?: string;
  /** Textarea ID for accessibility */
  id?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Auto focus on mount */
  autoFocus?: boolean;
  /** Number of visible rows */
  rows?: number;
  /** Whether the textarea can be resized */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
}

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaProps &
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, keyof TextareaProps>
>(
  (
    {
      size = 'md',
      leftDecorator,
      rightDecorator,
      disabled = false,
      isError = false,
      label,
      hintText,
      value,
      onChange,
      placeholder,
      debounceMs = 0,
      className,
      id,
      required = false,
      autoFocus = false,
      rows = 3,
      resize = 'vertical',
      ...rest
    },
    ref
  ) => {
    const shouldDebounce = debounceMs > 0;
    const [internalValue, setInternalValue] = useState(value);
    const debouncedOnChange = useDebouncedCallback(onChange, debounceMs);

    // Update internal value when external value changes
    useEffect(() => {
      if (shouldDebounce) {
        setInternalValue(value);
      }
    }, [value, shouldDebounce]);

    const handleTextareaChange = (
      e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      const newValue = e.target.value;
      if (shouldDebounce) {
        setInternalValue(newValue);
        debouncedOnChange(newValue);
      } else {
        onChange(newValue);
      }
    };

    // Styled container that holds the textarea and decorators
    const textareaContainerClasses = cn(
      // Base container styles
      'flex w-full gap-2 rounded-md border bg-transparent transition-colors',
      {
        'px-2 py-1': size === 'sm',
        'px-3 py-2': size === 'md',
      },
      'focus-within:outline-none',
      // Default state
      'border-secondary focus-within:border-brand',
      // Error state
      isError && 'border-error focus-within:border-error',
      // Disabled state
      disabled && 'cursor-not-allowed bg-secondary opacity-50',
      // Handle decorators alignment
      leftDecorator || rightDecorator ? 'items-start' : 'items-center'
    );

    // Unstyled textarea that fills the container
    const textareaClasses = cn(
      'max-w-full flex-1 border-none bg-transparent p-0 outline-none',
      {
        'text-xs': size === 'sm',
        'text-sm': size === 'md',
      },
      'placeholder:text-tertiary',
      disabled && 'cursor-not-allowed',
      // Resize handling
      {
        'resize-none': resize === 'none',
        'resize-y': resize === 'vertical',
        'resize-x': resize === 'horizontal',
        resize: resize === 'both',
      }
    );

    // Decorator styles
    const decoratorClasses = cn(
      'flex items-center text-tertiary',
      size === 'sm' ? 'mt-0.5' : 'mt-1' // Align with first line of text
    );
    const leftDecoratorClasses = cn(decoratorClasses);
    const rightDecoratorClasses = cn(decoratorClasses);

    const labelClasses = cn(
      'mb-1 block text-sm font-medium text-primary',
      disabled && 'text-disabled'
    );

    const hintTextClasses = cn(
      'mt-1 text-xs',
      isError ? 'text-error' : 'text-tertiary'
    );

    const containerClasses = cn('w-full', className);

    return (
      <div className={containerClasses}>
        {label && (
          <Text as="label" className={labelClasses} variant="xs">
            {label}
            {required && <span className="ml-1 text-error">*</span>}
          </Text>
        )}

        <div className={textareaContainerClasses}>
          {leftDecorator && (
            <div className={leftDecoratorClasses}>{leftDecorator}</div>
          )}

          <textarea
            ref={ref}
            id={id}
            value={shouldDebounce ? internalValue : value}
            onChange={handleTextareaChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            autoFocus={autoFocus}
            rows={rows}
            className={textareaClasses}
            {...rest}
          />

          {rightDecorator && (
            <div className={rightDecoratorClasses}>{rightDecorator}</div>
          )}
        </div>

        {hintText && (
          <Text className={hintTextClasses} variant="xs" weight="normal">
            {hintText}
          </Text>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
